import React, { useMemo } from 'react';
import { Trash, PencilSimple } from '@phosphor-icons/react';
import { useApp } from '../../../../contexts/AppContext';
import { MODAL_TYPES } from '../../../../constants/modalTypes';
import { useData } from '../../../../hooks/useData';
import { TABLES } from '../../../../constants/supabaseConstants';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../utils/errorLogging';
import { supabase } from '../../../../config/supabase';
import { formatCurrencyShort } from '../../../../utils/formatters';
import { MASTER_ITEM_IDS } from '../../../../utils/autoProjectItems';
import styles from '../../TradeCard.module.css';

const SHADING_NAMES = {
  '6d2a28bd-408e-4909-ae1f-af3b3716c0ac': 'תריס חשמלי',
  '279db709-1ca1-4500-bfad-0c8ced20bde0': 'צלון',
  'ad30c611-a88d-4418-9aae-2e604c69836d': 'וילון',
};

function WindowRow({ window: win, linkedItems, onDelete, space }) {
  const { openModal } = useApp();

  const { totalCost, profile, opening, shadingName } = useMemo(() => {
    const items = linkedItems || [];
    const wi = items.find(i => i.master_item_id === MASTER_ITEM_IDS.WINDOW);
    const si = items.find(i => i.master_item_id !== MASTER_ITEM_IDS.WINDOW);

    return {
      totalCost: items.reduce((sum, i) => sum + (parseFloat(i.total_cost) || 0), 0),
      profile: wi?.selected_option?.['פרופיל'] || '',
      opening: wi?.selected_option?.['פתיחה'] || '',
      shadingName: si ? SHADING_NAMES[si.master_item_id] || '' : '',
    };
  }, [linkedItems]);

  const sizeStr = win.width && win.height ? `${win.width}×${win.height} ס״מ` : '';
  const details = [sizeStr, profile, opening, shadingName].filter(Boolean).join(' · ');

  return (
    <div className={styles.itemRow}>
      <div className={styles.itemLeft}>
        <span className={styles.itemName}>{win.window_name || 'חלון'}</span>
        {details && <span className={styles.itemOptions}>{details}</span>}
      </div>
      <div className={styles.itemRight}>
        {totalCost > 0 && <span className={styles.itemCost}>{formatCurrencyShort(totalCost)}</span>}
        <div className={styles.itemActions}>
          <button
            className={styles.itemActionBtn}
            onClick={() =>
              openModal(MODAL_TYPES.WINDOW_FORM, {
                spaceId: win.space_id,
                space: space || { id: win.space_id },
                window: { ...win, _linkedItems: linkedItems },
              })
            }
            title="ערוך"
          >
            <PencilSimple size={14} />
          </button>
          <button
            className={`${styles.itemActionBtn} ${styles.danger}`}
            onClick={() => onDelete(win.id)}
            title="מחק חלון"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SpaceWindowsTable({ projectWindows, windowItemsMap = {}, space }) {
  const { remove: deleteWindow } = useData(TABLES.PROJECT_WINDOWS);
  const { logError } = useErrorLogging();

  const handleDeleteWindow = async id => {
    if (!window.confirm('האם למחוק את החלון?')) return;
    try {
      await supabase
        .from('project_items')
        .update({ deleted_at: new Date().toISOString() })
        .eq('details->>window_id', id);

      await deleteWindow({ id });
    } catch (error) {
      logError({
        error,
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: 'Failed to delete window',
        context: { windowId: id },
      });
    }
  };

  return (
    <div>
      {projectWindows?.length > 0 && (
        <div className={styles.itemList}>
          {projectWindows.map(w => (
            <WindowRow
              key={w.id}
              window={w}
              linkedItems={windowItemsMap[w.id]}
              onDelete={handleDeleteWindow}
              space={space}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SpaceWindowsTable;
